import { useHybridTestingAvailability } from "../../../hooks/useHybridTestingAvailability";
import type { AnalyticsOwnerKind } from "../../../lib/analyticsOwner";
import { cn } from "../../../lib/utils";
import { Alert, AlertDescription } from "../../ui/alert";

interface PlanAttribute {
  reference: string;
  statement: string;
}

interface UntestedAttributesNoticeProps {
  /** References of the attributes no Attribute Test or Analytics column answers, in plan order. */
  untestedReferences: readonly string[];
  attributes: readonly PlanAttribute[] | null | undefined;
  analyticsOwnerKind: AnalyticsOwnerKind;
  className?: string;
}

interface UntestedAttribute {
  reference: string;
  statement: string | undefined;
}

const untestedAttributes = (
  untestedReferences: readonly string[],
  attributes: readonly PlanAttribute[] | null | undefined,
): UntestedAttribute[] => {
  const statements = new Map(
    (attributes ?? []).map((a) => [a.reference, a.statement]),
  );
  return untestedReferences.map((reference) => ({
    reference,
    statement: statements.get(reference)?.trim() || undefined,
  }));
};

// Withheld while the hybrid-testing flags are off or still loading. Once they
// resolve off, the assistant proposes no Analytics column, so the notice does
// not suggest asking for one.
const addColumnHint = (offerAnalytics: boolean, target: "it" | "each") =>
  offerAnalytics
    ? `Add an Attribute Test column for ${target}, or ask the assistant to add an Analytics column`
    : `Add an Attribute Test column for ${target}`;

const singleNoticeText = (
  { reference, statement }: UntestedAttribute,
  offerAnalytics: boolean,
) =>
  `Attribute ${statement ? `${reference} (${statement})` : reference} has no test column. ${addColumnHint(offerAnalytics, "it")}.`;

/**
 * Names each attribute the plan proposes but no column tests, so it is caught
 * before generation rather than after. A table listing an attribute does not
 * test it. `role="status"` replaces the Alert's own `role="alert"`: it
 * describes the plan as it stands, not an error that just happened. Two or
 * more attributes are listed one per line because a run-on sentence of
 * statements is hard to scan.
 */
export function UntestedAttributesNotice({
  untestedReferences,
  attributes,
  analyticsOwnerKind,
  className,
}: UntestedAttributesNoticeProps) {
  const offerAnalytics =
    useHybridTestingAvailability(analyticsOwnerKind) !== "hidden";
  if (untestedReferences.length === 0) {
    return null;
  }
  const untested = untestedAttributes(untestedReferences, attributes);
  const [first, ...rest] = untested;
  return (
    <Alert
      role="status"
      className={cn("bg-amber-50 border-amber-200 p-3", className)}
    >
      <AlertDescription className="text-amber-900">
        {first && rest.length === 0 ? (
          singleNoticeText(first, offerAnalytics)
        ) : (
          <>
            <p>
              These attributes have no test column.{" "}
              {addColumnHint(offerAnalytics, "each")}:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {untested.map(({ reference, statement }) => (
                <li key={reference}>
                  <span className="font-semibold">{reference}</span>
                  {statement && ` — ${statement}`}
                </li>
              ))}
            </ul>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
